import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import * as THREE from 'three';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const InnerContainer = styled.div`
  max-width: 900px;
  width: 100%;
`;

const Header = styled.div`
  text-align: center;
  color: white;
  margin-bottom: 30px;

  h1 {
    font-size: 2.5em;
    margin-bottom: 10px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  }

  p {
    font-size: 1.1em;
    opacity: 0.9;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
`;

const UploadSection = styled.div`
  margin-bottom: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px dashed #667eea;

  h3 {
    margin-bottom: 15px;
    color: #333;
  }
`;

const FileInput = styled.input`
  display: block;
  width: 100%;
  padding: 12px;
  border: 2px solid #667eea;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  margin-bottom: 10px;

  &::-webkit-file-upload-button {
    background: #667eea;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 5px;
    cursor: pointer;
  }
`;

const ModelControls = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 10px;
  flex-wrap: wrap;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #333;
  }

  input[type="range"] {
    width: 120px;
  }

  span {
    min-width: 30px;
    font-weight: 600;
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const Button = styled.button`
  flex: 1;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  &:hover:not(:disabled) {
    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background: #e9ecef;
  color: #495057;

  &:hover {
    background: #dee2e6;
  }
`;

const ViewerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 500px;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const Video = styled.video`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Canvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const ARCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const QROverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 250px;
  height: 250px;
  border: 3px solid rgba(255, 255, 255, 0.6);
  border-radius: 20px;
  pointer-events: none;
`;

const QRCorner = styled.div`
  position: absolute;
  width: 30px;
  height: 30px;
  border: 4px solid #667eea;

  &.tl { top: -2px; left: -2px; border-right: none; border-bottom: none; border-radius: 20px 0 0 0; }
  &.tr { top: -2px; right: -2px; border-left: none; border-bottom: none; border-radius: 0 20px 0 0; }
  &.bl { bottom: -2px; left: -2px; border-right: none; border-top: none; border-radius: 0 0 0 20px; }
  &.br { bottom: -2px; right: -2px; border-left: none; border-top: none; border-radius: 0 0 20px 0; }
`;

const Status = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.bgColor || 'rgba(0, 0, 0, 0.8)'};
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 14px;
  backdrop-filter: blur(10px);
  max-width: 90%;
  text-align: center;
`;

const InfoBox = styled.div`
  padding: 20px;
  background: #e7f3ff;
  border-left: 4px solid #667eea;
  border-radius: 8px;
  margin-top: 20px;

  h4 {
    margin-bottom: 10px;
    color: #333;
  }

  ul {
    margin-left: 20px;
  }

  li {
    margin: 8px 0;
    color: #555;
  }

  a {
    color: #667eea;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default function ARDemoPage() {
  const [isRunning, setIsRunning] = useState(false);
  const [statusText, setStatusText] = useState('OBJ 파일을 업로드하고 시작 버튼을 클릭하세요');
  const [statusColor, setStatusColor] = useState('rgba(0, 0, 0, 0.8)');
  const [modelScale, setModelScale] = useState(1.0);
  const [modelRotation, setModelRotation] = useState(0);
  const [objContent, setObjContent] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const arCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const modelRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    return () => {
      stopAR();
    };
  }, []);

  const handleObjFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setObjContent(event.target.result);
        setStatusText('OBJ 파일 로드 완료! AR을 시작하세요.');
        setStatusColor('rgba(40, 167, 69, 0.9)');
      };
      reader.readAsText(file);
    }
  };

  const handleScaleChange = (e) => {
    const scale = parseFloat(e.target.value);
    setModelScale(scale);
    if (modelRef.current) {
      modelRef.current.scale.set(scale, scale, scale);
    }
  };

  const handleRotationChange = (e) => {
    const rotation = parseInt(e.target.value);
    setModelRotation(rotation);
    if (modelRef.current) {
      modelRef.current.rotation.y = (rotation * Math.PI) / 180;
    }
  };

  const startAR = async () => {
    if (!objContent) {
      alert('먼저 OBJ 파일을 업로드해주세요!');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: 1280, height: 720 }
      });

      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;

      video.addEventListener('loadedmetadata', () => {
        const canvas = canvasRef.current;
        const arCanvas = arCanvasRef.current;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        arCanvas.width = video.videoWidth;
        arCanvas.height = video.videoHeight;

        initThreeJS();
        setIsRunning(true);
        setStatusText('QR 코드를 스캔하세요...');
        setStatusColor('rgba(0, 123, 255, 0.9)');
      });
    } catch (err) {
      alert('카메라 접근 권한을 허용해주세요.');
      console.error(err);
    }
  };

  const stopAR = () => {
    setIsRunning(false);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (rendererRef.current) {
      rendererRef.current.dispose();
      rendererRef.current = null;
    }

    setStatusText('AR이 종료되었습니다');
    setStatusColor('rgba(0, 0, 0, 0.8)');
  };

  const initThreeJS = () => {
    const arCanvas = arCanvasRef.current;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      75,
      arCanvas.width / arCanvas.height,
      0.1,
      1000
    );
    camera.position.z = 5;
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({
      canvas: arCanvas,
      alpha: true,
      antialias: true
    });
    renderer.setSize(arCanvas.width, arCanvas.height);
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1);
    scene.add(directionalLight);

    loadOBJModel();
    tick();
  };

  const loadOBJModel = () => {
    try {
      const model = parseOBJ(objContent);
      model.scale.set(modelScale, modelScale, modelScale);
      model.rotation.y = (modelRotation * Math.PI) / 180;
      model.visible = false;

      sceneRef.current.add(model);
      modelRef.current = model;

      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      model.position.sub(center);

      setStatusText('3D 모델 로드 완료! QR 코드를 스캔하세요.');
      setStatusColor('rgba(40, 167, 69, 0.9)');
    } catch (err) {
      console.error('OBJ 로드 오류:', err);
      setStatusText('OBJ 파일 로드 실패. 파일을 확인해주세요.');
      setStatusColor('rgba(220, 53, 69, 0.9)');
    }
  };

  const parseOBJ = (text) => {
    const group = new THREE.Group();
    const geometry = new THREE.BufferGeometry();

    const vertices = [];
    const faces = [];

    const lines = text.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line || line.charAt(0) === '#') continue;

      const parts = line.split(/\s+/);
      const type = parts[0];

      if (type === 'v') {
        vertices.push(parseFloat(parts[1]));
        vertices.push(parseFloat(parts[2]));
        vertices.push(parseFloat(parts[3]));
      } else if (type === 'f') {
        for (let j = 1; j <= 3; j++) {
          const vertexIndex = parts[j].split('/')[0];
          faces.push(parseInt(vertexIndex) - 1);
        }
      }
    }

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(faces);
    geometry.computeVertexNormals();

    const material = new THREE.MeshPhongMaterial({
      color: 0x00ff00,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);

    return group;
  };

  const tick = () => {
    if (!isRunning) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // jsQR 라이브러리 사용
    if (window.jsQR) {
      const code = window.jsQR(imageData.data, imageData.width, imageData.height);

      if (code) {
        drawQRBox(ctx, code);

        if (modelRef.current) {
          modelRef.current.visible = true;
          updateModelPosition(code, canvas);
          setStatusText(`QR 코드 감지됨: ${code.data}`);
          setStatusColor('rgba(40, 167, 69, 0.9)');
        }
      } else {
        if (modelRef.current) {
          modelRef.current.visible = false;
        }
        setStatusText('QR 코드를 찾는 중...');
        setStatusColor('rgba(0, 123, 255, 0.9)');
      }
    }

    if (modelRef.current && modelRef.current.visible) {
      modelRef.current.rotation.y += 0.01;
    }

    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(tick);
  };

  const drawQRBox = (ctx, code) => {
    const { topLeftCorner, topRightCorner, bottomLeftCorner, bottomRightCorner } = code.location;

    ctx.strokeStyle = '#00ff00';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(topLeftCorner.x, topLeftCorner.y);
    ctx.lineTo(topRightCorner.x, topRightCorner.y);
    ctx.lineTo(bottomRightCorner.x, bottomRightCorner.y);
    ctx.lineTo(bottomLeftCorner.x, bottomLeftCorner.y);
    ctx.closePath();
    ctx.stroke();
  };

  const updateModelPosition = (code, canvas) => {
    const centerX = (code.location.topLeftCorner.x + code.location.bottomRightCorner.x) / 2;
    const centerY = (code.location.topLeftCorner.y + code.location.bottomRightCorner.y) / 2;

    const ndcX = (centerX / canvas.width) * 2 - 1;
    const ndcY = -(centerY / canvas.height) * 2 + 1;

    modelRef.current.position.x = ndcX * 3;
    modelRef.current.position.y = ndcY * 3;
    modelRef.current.position.z = 0;
  };

  return (
    <Container>
      <InnerContainer>
        <Header>
          <h1>📦 QR 코드 AR Viewer</h1>
          <p>OBJ 파일을 업로드하고 QR 코드를 스캔하세요</p>
        </Header>

        <Card>
          <UploadSection>
            <h3>🎨 3D 모델 업로드</h3>
            <FileInput type="file" accept=".obj" onChange={handleObjFile} />
            <ModelControls>
              <label>
                크기:
                <input
                  type="range"
                  min="0.1"
                  max="5"
                  step="0.1"
                  value={modelScale}
                  onChange={handleScaleChange}
                />
                <span>{modelScale.toFixed(1)}</span>
              </label>
              <label>
                회전:
                <input
                  type="range"
                  min="0"
                  max="360"
                  step="1"
                  value={modelRotation}
                  onChange={handleRotationChange}
                />
                <span>{modelRotation}°</span>
              </label>
            </ModelControls>
          </UploadSection>

          <Controls>
            <PrimaryButton onClick={startAR} disabled={isRunning || !objContent}>
              📷 AR 시작
            </PrimaryButton>
            <SecondaryButton onClick={stopAR} disabled={!isRunning}>
              ⏹ 종료
            </SecondaryButton>
          </Controls>

          <ViewerContainer>
            <Video ref={videoRef} autoPlay playsInline />
            <Canvas ref={canvasRef} />
            <ARCanvas ref={arCanvasRef} />
            <QROverlay>
              <QRCorner className="tl" />
              <QRCorner className="tr" />
              <QRCorner className="bl" />
              <QRCorner className="br" />
            </QROverlay>
            <Status bgColor={statusColor}>{statusText}</Status>
          </ViewerContainer>

          <InfoBox>
            <h4>💡 사용 방법</h4>
            <ul>
              <li><strong>1단계:</strong> OBJ 파일을 업로드하세요</li>
              <li><strong>2단계:</strong> "AR 시작" 버튼을 클릭하세요</li>
              <li><strong>3단계:</strong> QR 코드를 카메라에 비추면 3D 모델이 나타납니다</li>
              <li><strong>팁:</strong> QR 코드는 <a href="https://www.qr-code-generator.com/" target="_blank" rel="noopener noreferrer">여기서</a> 생성할 수 있습니다</li>
            </ul>
          </InfoBox>
        </Card>
      </InnerContainer>
    </Container>
  );
}
